import { BillingProcess, ProcessApiResponse, BackgroundProcessResponse, SyncProcessResponse } from '../interfaces/billing.rpm';
import { ClaimStage } from '../interfaces/billing';
import { ProcessPollingConfig } from '../types/process';
import { BillingService } from './index';
import { POLLING_INTERVALS } from '../components/rpms/constants';

// Use BillingProcess directly for the callback type
type ProcessPollingCallback = (process: BillingProcess) => void;

/**
 * Service for managing process polling and status updates
 */
export class ProcessPollingService {
  private pollingInterval: NodeJS.Timeout | null = null;
  private processCallbacks: Map<number, ProcessPollingCallback[]> = new Map();
  private allProcessesCallback: ((processes: BillingProcess[]) => void) | null = null;
  private isPolling: boolean = false;
  private retryCount: number = 0;
  private billingService: BillingService;
  
  // Default configuration
  private defaultConfig: ProcessPollingConfig = {
    intervalMs: POLLING_INTERVALS.ACTIVE_PROCESS,
    maxAttempts: POLLING_INTERVALS.MAX_RETRY_ATTEMPTS,
    timeoutMs: POLLING_INTERVALS.TIMEOUT,
  };

  constructor() {
    this.billingService = new BillingService();
  }

  /**
   * Start polling for all processes with optional specific process callback
   */
  startPolling(
    processId: number,
    callback: ProcessPollingCallback,
    config: Partial<ProcessPollingConfig> = {}
  ): void {
    // Add callback for specific process
    const callbacks = this.processCallbacks.get(processId) || [];
    callbacks.push(callback);
    this.processCallbacks.set(processId, callbacks);
    
    // Start RPM process polling if not already running
    this.startRpmProcessPolling(config);
  }

  /**
   * Start polling for all RPM processes
   */
  startRpmProcessPolling(
    config: Partial<ProcessPollingConfig> = {},
    allProcessesCallback?: (processes: BillingProcess[]) => void
  ): void {
    if (this.isPolling) {
      if (allProcessesCallback) {
        this.allProcessesCallback = allProcessesCallback;
      }
      return;
    }
    
    const pollingConfig = { ...this.defaultConfig, ...config };
    this.isPolling = true;
    this.retryCount = 0;
    
    if (allProcessesCallback) {
      this.allProcessesCallback = allProcessesCallback;
    }
    
    // Start polling all processes
    this.pollingInterval = setInterval(async () => {
      try {
        const processes = await this.fetchAllProcesses();
        
        if (processes) {
          // Reset retry count on successful fetch
          this.retryCount = 0;
          
          // Call the all processes callback if set
          if (this.allProcessesCallback) {
            this.allProcessesCallback(processes);
          }
          
          // Call individual process callbacks
          processes.forEach(process => {
            const callbacks = this.processCallbacks.get(process.id);
            if (callbacks) {
              callbacks.forEach(callback => callback(process));
              
              // Remove callbacks for processes in final state
              if (this.isFinalState(process.status)) {
                this.processCallbacks.delete(process.id);
              }
            }
          });
          
          // Stop polling if no more active callbacks
          if (this.processCallbacks.size === 0 && !this.allProcessesCallback) {
            this.stopRpmProcessPolling();
          }
        }
      } catch (error) {
        this.handlePollingError(error as Error, pollingConfig);
      }
    }, pollingConfig.intervalMs);
    
    // Set timeout to stop polling after maximum time
    setTimeout(() => {
      if (this.isPolling) {
        this.stopRpmProcessPolling();
      }
    }, pollingConfig.timeoutMs);
  }

  /**
   * Stop polling for a specific process
   */
  stopPolling(processId: number): void {
    this.processCallbacks.delete(processId);
    
    // Stop global polling if no more callbacks
    if (this.processCallbacks.size === 0 && !this.allProcessesCallback) {
      this.stopRpmProcessPolling();
    }
  }

  /**
   * Stop RPM process polling
   */
  stopRpmProcessPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    this.retryCount = 0;
    this.allProcessesCallback = null;
  }

  /**
   * Stop all active polling
   */
  stopAllPolling(): void {
    this.processCallbacks.clear();
    this.stopRpmProcessPolling();
  }

  /**
   * Check if a process is currently being polled
   */
  isProcessPolling(processId: number): boolean {
    return this.processCallbacks.has(processId);
  }

  /**
   * Check if global polling is active
   */
  isGlobalPolling(): boolean {
    return this.isPolling;
  }

  /**
   * Get all active polling process IDs
   */
  getActivePollingProcesses(): number[] {
    return Array.from(this.processCallbacks.keys());
  }

  /**
   * Fetch all processes from API
   */
  private async fetchAllProcesses(): Promise<BillingProcess[] | null> {
    try {
      const processes = await this.billingService.getProcesses();
      return processes || [];
    } catch (error) {
      console.error('Error fetching all processes:', error);
      throw error;
    }
  }

  /**
   * Handle polling errors with retry logic
   */
  private handlePollingError(
    error: Error,
    config: ProcessPollingConfig
  ): void {
    if (this.retryCount >= config.maxAttempts) {
      // Max retries reached, stop polling
      console.error('Max retries reached, stopping polling');
      this.stopRpmProcessPolling();
      return;
    }
    
    // Increment retry count
    this.retryCount++;
    
    // Log retry attempt
    console.warn(`Retrying processes polling (attempt ${this.retryCount}/${config.maxAttempts})`);
  }

  /**
   * Check if a process status is in final state
   */
  private isFinalState(status: ClaimStage): boolean {
    return [
      ClaimStage.Created,
      ClaimStage.Exported,
      ClaimStage.Discarded,
    ].includes(status);
  }

  /**
   * Check if a process status is in active state
   */
  public isActiveState(status: ClaimStage): boolean {
    return [
      ClaimStage.CreationInProgress,
      ClaimStage.ExportInProgress,
    ].includes(status);
  }

  /**
   * Get smart polling interval based on process status
   */
  public getSmartPollingInterval(status: ClaimStage): number {
    switch (status) {
      case ClaimStage.CreationInProgress:
      case ClaimStage.ExportInProgress:
        return POLLING_INTERVALS.ACTIVE_PROCESS;
      default:
        return POLLING_INTERVALS.INACTIVE_PROCESS;
    }
  }


  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopAllPolling();
    this.processCallbacks.clear();
  }
}

// Export singleton instance
export const processPollingService = new ProcessPollingService();

// Utility functions for working with API responses
export const ProcessResponseUtils = {
  /**
   * Type guard for background response
   */
  isBackgroundResponse(response: ProcessApiResponse): response is BackgroundProcessResponse {
    return 'mode' in response && response.mode === 'background';
  },

  /**
   * Type guard for sync response
   */
  isSyncResponse(response: ProcessApiResponse): response is SyncProcessResponse {
    return 'processId' in response && !('mode' in response);
  },

  /**
   * Extract process ID from any response type
   */
  getProcessId(response: ProcessApiResponse): number {
    if (this.isBackgroundResponse(response)) {
      return response.process.id;
    }
    return response.processId;
  },

  /**
   * Extract process object from background response
   */
  getProcess(response: BackgroundProcessResponse): BillingProcess {
    return response.process;
  },

  /**
   * Create process summary from sync response
   */
  createProcessSummary(response: SyncProcessResponse): {
    processId: number;
    summary: string;
  } {
    const { processId, created, failed, exported } = response;
    let summary = '';
    
    if (created !== undefined) {
      summary = `Created: ${created}${failed ? `, Failed: ${failed}` : ''}`;
    } else if (exported !== undefined) {
      summary = `Exported: ${exported}${failed ? `, Failed: ${failed}` : ''}`;
    }
    
    return { processId, summary };
  },
};

export default ProcessPollingService;